import * as React from 'react'
import { useSnapshot } from 'valtio'
import { historyState } from '../../../../store/historyState.ts'
import { dataState } from '../../../../store/dataState.ts'
import { isPointLocationValid } from '../../../../helpers/locationStatusHelper.ts'
import { IPoint } from '../../../../interfaces/types.ts'
import { HistoryCard } from './HistoryCard.tsx'
import '../History.css'

interface Props {
    /** Called when the user swipes near the end of loaded data */
    onReachEnd?: () => void
    /** Whether more data is currently loading */
    loading?: boolean
}

const DEFAULT_ZOOM = 19

export const HistoryCarousel: React.FC<Props> = ({ onReachEnd, loading }) => {
    const locations = (useSnapshot(historyState) as typeof historyState).locations
    const selectedPetId = (useSnapshot(historyState) as typeof historyState).selectedPetId
    const pets = (useSnapshot(dataState) as typeof dataState).pets
    const map = (useSnapshot(historyState) as typeof historyState).map
    const noMoreOlder = (useSnapshot(historyState) as typeof historyState).noMoreOlder

    const pet = pets.find(p => p._id === selectedPetId)
    const scrollRef = React.useRef<HTMLDivElement>(null)
    const [activeIndex, setActiveIndex] = React.useState(0)
    const reachEndFiredRef = React.useRef(false)
    const prevFirstIdRef = React.useRef<string>('')

    const initialZoomSet = React.useRef(false)

    // Reset initial zoom flag when locations are fully replaced
    React.useEffect(() => {
        const firstId = locations[0]?._id || ''

        if (firstId && firstId === prevFirstIdRef.current && locations.length > 0) {
            reachEndFiredRef.current = false
        } else {
            setActiveIndex(0)
            reachEndFiredRef.current = false
            initialZoomSet.current = false
            if (scrollRef.current) {
                scrollRef.current.scrollLeft = 0
            }
        }

        prevFirstIdRef.current = firstId
    }, [locations])

    // Sync highlight, smart-pan, and visible marker window when activeIndex changes
    React.useEffect(() => {
        const point = locations[activeIndex]
        if (!point) {
            historyState.highlight = null
            return
        }

        if (isPointLocationValid(point)) {
            historyState.highlight = point
            panToPoint(point)
        } else {
            historyState.highlight = null
        }

        // Keep the visible marker window centred around the active card (±5, min 0)
        const windowStart = Math.max(0, activeIndex - 4)
        historyState.markerWindowStart = windowStart
    }, [activeIndex, locations])

    // Trigger onReachEnd when approaching the last item
    React.useEffect(() => {
        if (
            onReachEnd &&
            !reachEndFiredRef.current &&
            !noMoreOlder &&
            locations.length > 0 &&
            activeIndex >= locations.length - 2
        ) {
            reachEndFiredRef.current = true
            onReachEnd()
        }
    }, [activeIndex, locations.length, onReachEnd, noMoreOlder])

    /** Pan the map to always center the active marker */
    const panToPoint = (point: IPoint) => {
        if (!map) return

        const latLng = new google.maps.LatLng(
            point.properties.latitude,
            point.properties.longitude
        )

        if (!initialZoomSet.current) {
            map.setZoom(DEFAULT_ZOOM)
            initialZoomSet.current = true
        }

        map.panTo(latLng)
    }

    /** Handle scroll-snap settling to determine active index */
    const handleScroll = () => {
        if (!scrollRef.current) return

        const container = scrollRef.current
        const cardWidth = container.offsetWidth
        const scrollLeft = container.scrollLeft
        const newIndex = Math.round(scrollLeft / cardWidth)

        if (newIndex !== activeIndex && newIndex >= 0 && newIndex < locations.length) {
            setActiveIndex(newIndex)
        }
    }

    // Empty / no pet state
    if (!pet || !locations.length) {
        return (
            <div className={'w-full px-4 py-3 bg-base-100'}>
                <div className={'text-center text-primary text-sm'}>
                    {!pet ? 'Select a pet to view history' : 'No history available for this period'}
                </div>
            </div>
        )
    }

    // Single point — no swiping needed
    if (locations.length === 1) {
        return (
            <div className={'w-full px-3 pb-1'}>
                <HistoryCard
                    location={locations[0]}
                    pet={pet}
                    index={0}
                    total={1}
                />
            </div>
        )
    }

    return (
        <div className={'w-full flex flex-col gap-1'}>
            {/* Scrollable card container */}
            <div
                ref={scrollRef}
                className={'flex overflow-x-auto snap-x snap-mandatory gap-0 history-carousel-scroll'}
                style={{
                    scrollbarWidth: 'none',
                    WebkitOverflowScrolling: 'touch',
                    msOverflowStyle: 'none',
                }}
                onScroll={handleScroll}
            >
                {locations.map((location, i) => (
                    <div
                        key={location._id || `loc-${i}`}
                        className={'snap-center shrink-0 w-full px-3'}
                    >
                        <HistoryCard
                            location={location}
                            pet={pet}
                            index={i}
                            total={locations.length}
                        />
                    </div>
                ))}

                {/* Loading indicator at the end */}
                {loading && (
                    <div className={'snap-center shrink-0 w-full px-3 flex items-center justify-center py-4'}>
                        <span className="loading loading-spinner loading-sm text-primary"></span>
                    </div>
                )}
            </div>

            {/* Dot indicators for small sets, counter text for larger */}
            <div className={'flex justify-center pb-1'}>
                {locations.length <= 15 ? (
                    <div className={'flex items-center gap-1'}>
                        {locations.map((_, i) => (
                            <div
                                key={`dot-${i}`}
                                className={`rounded-full transition-all duration-200 ${
                                    i === activeIndex
                                        ? 'w-2 h-2 bg-primary'
                                        : 'w-1.5 h-1.5 bg-primary/30'
                                }`}
                            />
                        ))}
                    </div>
                ) : (
                    <span className={'text-xs text-base-content/50'}>
                        {activeIndex + 1} of {locations.length}{noMoreOlder ? '' : '+'}
                    </span>
                )}
            </div>
        </div>
    )
}
